import { orderBy, query } from 'firebase/firestore';
import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useCollection } from '@/lib/firestore/hooks';
import { companiesCol, contactsCol, contractsCol } from '@/lib/firestore/refs';
import { KpiCard, Skeleton } from '@/lib/ui';
import type { Company, Contact, Contract } from '@shared/types';
import { contractExpiry } from '../contracts/expiry';

/** Phase 3b adds case KPIs here. */
export const CRM_HOME_EXTRA: (() => ReactNode)[] = [];
const TIERS = ['platinum', 'gold', 'silver', 'bronze'] as const;

export function CrmHomePage() {
  const companies = useCollection<Company>(useMemo(() => query(companiesCol(), orderBy('name')), []), []);
  const contacts = useCollection<Contact>(useMemo(() => query(contactsCol(), orderBy('name')), []), []);
  const contracts = useCollection<Contract>(useMemo(() => query(contractsCol(), orderBy('endDate')), []), []);
  if (companies.loading || contracts.loading) return <Skeleton lines={6} />;
  const active = companies.data.filter((c) => c.status === 'active');
  const activeContracts = contracts.data.filter((c) => c.status === 'active');
  const expiring = activeContracts.filter((c) => contractExpiry(c.endDate) !== 'active');
  return (
    <>
      <div className="page-header"><div><div className="page-title">Customer Service</div><div className="page-subtitle">Accounts, contacts, contracts and cases in one place</div></div></div>
      <div className="kpi-grid">
        <KpiCard label="Active companies" value={active.length} />
        <KpiCard label="Contacts" value={contacts.data.length} />
        <KpiCard label="Active contracts" value={activeContracts.length} />
        <KpiCard label="Contracts expiring ≤ 30 days" value={expiring.length} trend={expiring.length ? 'Review renewals' : undefined} />
      </div>
      {CRM_HOME_EXTRA.map((render, i) => <span key={i}>{render()}</span>)}
      <div className="two-col-layout">
        <div className="card">
          <div className="card-header"><span className="card-title">Companies by tier</span></div>
          <div className="card-body">{TIERS.map((t) => <div key={t} className="settings-row"><div className="settings-label">{t}</div><div>{active.filter((c) => c.tier === t).length}</div></div>)}</div>
        </div>
        <div className="card">
          <div className="card-header"><span className="card-title">Renewals due</span></div>
          <div className="card-body">
            {expiring.length === 0 ? <div className="empty-state">No contracts expiring soon.</div> : expiring.map((c) => (
              <div key={c.id} className="settings-row"><div><Link to={`/crm/contracts/${c.id}`}>{c.name}</Link><div className="settings-desc">{c.companyId} · ends {c.endDate}</div></div></div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
